import { ReactElement } from 'react';
import { Box, Typography } from '@mui/material';
import { Address } from 'viem';

import { mapDataToUndefinedIfSkip } from 'modules/api/utils';
import { useConnection } from 'modules/auth/hooks/useConnection';
import { useGlobalTranslation } from 'modules/i18n';
import {
  EMPTY_POOL_UNSTAKES,
  useGetPendingUnstakesQuery,
} from 'modules/pool/actions/getPendingUnstakes';

import { StakingTable as Table } from '../StakingTable';
import { TableRow } from './TableRow';
import { translation } from './translation';

interface IPendingDelegateStakingProps {
  pools: Address[];
}

export function PendingDelegateStaking({
  pools,
}: IPendingDelegateStakingProps): ReactElement | null {
  const { t, keys } = useGlobalTranslation(translation);

  const { isConnected, connectionKey, cacheTime, isSafeAccount } =
    useConnection();
  const { data: poolUnstakes = EMPTY_POOL_UNSTAKES } =
    useGetPendingUnstakesQuery(
      {
        connectionKey,
        poolAddresses: pools,
      },
      {
        skip: !isConnected || !pools.length,
        selectFromResult: mapDataToUndefinedIfSkip,
        refetchOnMountOrArgChange: cacheTime,
        pollingInterval: isSafeAccount ? cacheTime : undefined,
      },
    );

  if (!poolUnstakes?.length) {
    return null;
  }

  return (
    <Box
      sx={theme => ({
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(4),
      })}
    >
      <Typography fontWeight={600} textTransform="uppercase" variant="body1">
        {t(keys.title)}
      </Typography>

      <Table>
        <Table.Head>
          <Table.Row>
            <Table.HeadCell>{t(keys.asset)}</Table.HeadCell>

            <Table.HeadCell>{t(keys.amount)}</Table.HeadCell>

            <Table.HeadCell>{t(keys.initiated)}</Table.HeadCell>

            <Table.HeadCell> </Table.HeadCell>
          </Table.Row>
        </Table.Head>

        <Table.Body>
          {poolUnstakes.map(unstake => (
            <TableRow key={unstake.id} poolUnstake={unstake} />
          ))}
        </Table.Body>
      </Table>
    </Box>
  );
}
